// Confidence levels based on AI processing
export type ConfidenceLevel = 'high' | 'medium' | 'low';

// Confidence thresholds for case routing and visibility
export const CONFIDENCE_THRESHOLDS = {
  MINIMUM_VISIBILITY: 85, // Cases below this are hidden (WORK-002)
  HIGH_CONFIDENCE: 90, // High confidence cases
  MEDIUM_CONFIDENCE: 75, // Medium confidence cases
  LOW_CONFIDENCE: 60, // Low confidence threshold
} as const;

// Task #16: Error handling constants and configurations
export const ERROR_CODES = {
  // Confidence calculation errors
  CONFIDENCE_CALC_FAILED: 'CONF_CALC_001',
  CONFIDENCE_INVALID_RANGE: 'CONF_CALC_002',
  CONFIDENCE_METADATA_CORRUPT: 'CONF_CALC_003',

  // Routing errors
  ROUTING_THRESHOLD_FAILED: 'ROUTE_001',
  ROUTING_WORKFLOW_INVALID: 'ROUTE_002',
  ROUTING_ASSIGNMENT_FAILED: 'ROUTE_003',

  // Flagging errors
  FLAG_DYNAMICS_UNREACHABLE: 'FLAG_001',
  FLAG_INVALID_CASE_ID: 'FLAG_002',
  FLAG_PERMISSION_DENIED: 'FLAG_003',
  FLAG_RATE_LIMITED: 'FLAG_004',

  // System errors
  NETWORK_TIMEOUT: 'SYS_001',
  API_UNAVAILABLE: 'SYS_002',
  DATA_CORRUPTION: 'SYS_003',
  AUTHENTICATION_FAILED: 'SYS_004',
} as const;

export const DEFAULT_RETRY_STRATEGIES = {
  IMMEDIATE: {
    type: 'immediate' as const,
    baseDelay: 0,
    maxDelay: 0,
    multiplier: 1,
    jitter: false,
  },
  EXPONENTIAL_BACKOFF: {
    type: 'exponential_backoff' as const,
    baseDelay: 1000,
    maxDelay: 30000,
    multiplier: 2,
    jitter: true,
  },
  FIXED_INTERVAL: {
    type: 'fixed_interval' as const,
    baseDelay: 5000,
    maxDelay: 5000,
    multiplier: 1,
    jitter: false,
  },
  MANUAL_ONLY: {
    type: 'manual_only' as const,
    baseDelay: 0,
    maxDelay: 0,
    multiplier: 1,
    jitter: false,
  },
} as const;

// Case filtering options for confidence-based routing
export interface CaseFilters {
  confidenceLevel?: ConfidenceLevel;
  minConfidence?: number;
  assignedToCurrentUser?: boolean;
  status?: PayrollCase['status'];
}

export interface PayrollCase {
  caseNumber: string;
  customer: string;
  customerFein?: string; // Federal Employer Identification Number (9 digits)
  cenCoid?: string; // Tenant ID for Core API routing
  status: 'Pending' | 'In Progress' | 'Processing' | 'Completed' | 'Failed';
  aiConfidence: number;
  created: string;
  assignedTo: string; // Pre-assigned from Dynamics (WORK-001)
  description?: string;
  priority?: 'low' | 'medium' | 'high';
  lastUpdated?: string;
  caseOriginCode?: number; // Dynamics case origin code (integer)
  caseType?: string; // Human-readable case type (e.g., "Email", "Phone", "Internal")
  hasAttachments?: boolean; // Whether case has attachments
  isProcessed?: boolean; // Whether initial case processing is complete (OCR, LLM, etc.)
  // Core API integration validation
  canPreviewPayroll?: boolean; // False if FEIN or tenant missing
  manualProcessingReason?: string; // Reason why manual processing is required

  // OCR+LLM Processing Status (for automated background processing)
  processingStatus?: 'pending' | 'processing' | 'completed' | 'failed' | 'n/a';
  currentStage?: string | null; // e.g., "ocr_extraction", "llm_extraction", "finalized", "error"

  // Enhanced confidence tracking metadata (Task #10)
  confidenceMetadata?: ConfidenceMetadata;
  processingMetadata?: ProcessingMetadata;
  routingInfo?: CaseRoutingInfo;
  qualityAssurance?: QualityAssuranceInfo;

  // Payroll submission tracking (prevents reprocessing)
  payrollSubmittedAt?: string; // When payroll was submitted to isolved (null = not yet)
  payrollSubmittedBy?: string; // Email of user who submitted

  // Core API employees for dropdown selection
  coreEmployeesRaw?: CoreEmployee[]; // Raw employee list from Core API

  // Core API dashboard notes for critical alerts
  coreDashboardNotesRaw?: DashboardNote[]; // Raw dashboard notes from Core API

  // Manual processing lock status (from ManualProcessingLog table)
  isLockedForManualProcessing?: boolean; // True = case is locked, all modifications blocked
  manualProcessingLockReason?: string; // Reason code (e.g., 'document_unclear', 'other')
  manualProcessingLockComment?: string; // Additional comment provided when locked
  manualProcessingLockedBy?: string; // Email of user who locked the case
  manualProcessingLockedAt?: string; // When the case was locked (ISO timestamp)

  // Latest 'significant' payroll feedback (Save submissions with selections or comments)
  latestFeedbackBy?: string; // Email of user who submitted feedback
  latestFeedbackAt?: string; // When feedback was submitted (ISO timestamp)
  latestFeedbackIssues?: string[]; // Snake_case keys (e.g., 'manual_new_hire')
  latestFeedbackComment?: string; // Optional free-form context

  // Business rule block status (from PayrollCase table)
  // Cases blocked due to company configuration issues (e.g., duplicate EINs)
  isBlocked?: boolean; // True = case is blocked due to business rule violation (permanent, no retry)
  blockedReasonType?: string; // Error type code (e.g., 'duplicate_ein')
  blockedReasonMessage?: string; // User-friendly rule message displayed in UI banner
  blockedAt?: string; // When the case was blocked (ISO timestamp)
}

// Core API employee structure for dropdown selection
export interface CoreEmployee {
  id: number;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  payGroupId?: number;
  payGroupName?: string;
}

// Core API dashboard note structure
export interface DashboardNote {
  id: number;
  acknowledged: boolean;
  acknowledgedNotes: string | null;
  clientId: number;
  effectiveDate: string;
  expirationDate: string | null;
  note: string;
  priority: number; // 0 = critical
  resolved: boolean;
  resolvedNote: string | null;
  type: number;
}

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: string;
}

export interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

export interface DashboardStats {
  totalCases: number;
  openCases: number;
  resolvedCases: number;
  averageResolutionTime: string;
}

// Template-driven entry items (new dynamic table)
export interface PayItemValue {
  id: string; // UUID
  payItemId: number;
  payItemTitle: string;
  displayOrder: number;
  value: string | null;
  valueType: 'hours' | 'dollars';
  // True when the source expressed this value as HH:MM (e.g. "40:15" → stored
  // as "40.15" with this flag set). Display-only flag: controls whether the
  // grid renders the value with a colon; does not affect the numeric payload
  // sent to the Core API.
  isHoursMinutes?: boolean;
}

export interface EmployeeGroup {
  employeeGroupId: number;
  employeeId: string | null;
  employeeName: string | null;
  employeeNumber: string | null;
  employeeType: string | null;
  payItems: PayItemValue[];
}

export interface PayGroupTab {
  payGroupId: number;
  payGroupName: string;
  employees: EmployeeGroup[];
}

// One displayable column derived from the pay-item template.  A pay item
// with both showHours AND showDollars produces TWO TemplateColumn entries
// that share the same payItemId/displayOrder but differ in valueType.
// The backend always sorts hours before dollars within a dual pair.
export interface TemplateColumn {
  payItemId: number;
  title: string;
  displayOrder: number;
  valueType: 'hours' | 'dollars';
}

export interface EntryItemsResponse {
  success: boolean;
  caseId: number;
  payGroups: PayGroupTab[];
  templateName: string | null;
  templateColumns?: TemplateColumn[];
}

export interface PayrollEntry {
  id: string;
  attachmentId?: number; // Document/attachment this entry belongs to
  employeeName: string;
  employeeId: string;
  employeeType?: 'hourly' | 'salary'; // Optional, defaults to 'hourly'

  // Hour types (all optional with default 0)
  regularHours: number;
  overtimeHours: number;
  sickHours?: number; // Optional, defaults to 0
  vacationHours?: number; // Optional, defaults to 0
  holidayHours?: number; // Optional, defaults to 0
  personalHours?: number; // Optional, defaults to 0
  otherHours?: number; // Optional, defaults to 0

  // Rates and pay
  regularRate: number;
  overtimeRate: number;
  salaryAmount?: number;
  totalPay: number;

  // Additional earnings
  bonusEarnings?: number;
  otherEarnings?: number;

  department?: string;
  isEdited?: boolean;
  isRemoved?: boolean;
  deletedByAgent?: boolean; // Soft delete flag - if true, entry is marked for deletion

  // Contains full extracted data with bounding boxes for highlighting
  extracted_data?: {
    [fieldName: string]: any; // Each field can have {value, bounding_box, page_number, confidence}
  };
}

export interface ValidationErrors {
  [key: string]: {
    regularHours?: string;
    overtimeHours?: string;
    sickHours?: string;
    vacationHours?: string;
    holidayHours?: string;
    personalHours?: string;
    otherHours?: string;
    regularRate?: string;
    overtimeRate?: string;
    salaryAmount?: string;
    bonusEarnings?: string;
    otherEarnings?: string;
  };
}

export interface CaseDocument {
  id: string;
  name: string;
  type: 'pdf' | 'excel' | 'image';
  url: string;
  size: string;
  uploadedAt: string;
}

// Helper functions for confidence-based operations
export const getConfidenceLevel = (confidence: number): ConfidenceLevel => {
  if (confidence >= CONFIDENCE_THRESHOLDS.HIGH_CONFIDENCE) return 'high';
  if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM_CONFIDENCE) return 'medium';
  return 'low';
};

export const isValidForProcessing = (confidence: number): boolean => {
  return confidence >= CONFIDENCE_THRESHOLDS.MINIMUM_VISIBILITY;
};

// Task #16: Error handling utility functions
export const createConfidenceError = (
  errorType: ConfidenceError['errorType'],
  errorCode: string,
  message: string,
  options: Partial<ConfidenceError> = {}
): ConfidenceError => {
  const baseStrategy = getDefaultRetryStrategy(errorType);

  return {
    id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    errorType,
    errorCode,
    errorMessage: message,
    severity: options.severity || 'error',
    recoverable: options.recoverable ?? true,
    retryCount: 0,
    maxRetries: options.maxRetries || getDefaultMaxRetries(errorType),
    retryStrategy: options.retryStrategy || baseStrategy,
    context: {
      operation: options.context?.operation || errorType,
      parameters: options.context?.parameters,
      stackTrace: options.context?.stackTrace,
      userAction: options.context?.userAction,
    },
    ...options,
  };
};

export const getDefaultRetryStrategy = (
  errorType: ConfidenceError['errorType']
): RetryStrategy => {
  switch (errorType) {
    case 'confidence_calculation':
    case 'threshold_evaluation':
      return DEFAULT_RETRY_STRATEGIES.EXPONENTIAL_BACKOFF;
    case 'flagging_failed':
      return DEFAULT_RETRY_STRATEGIES.FIXED_INTERVAL;
    case 'confidence_routing':
      return DEFAULT_RETRY_STRATEGIES.IMMEDIATE;
    case 'metadata_update':
      return DEFAULT_RETRY_STRATEGIES.EXPONENTIAL_BACKOFF;
    default:
      return DEFAULT_RETRY_STRATEGIES.EXPONENTIAL_BACKOFF;
  }
};

export const getDefaultMaxRetries = (
  errorType: ConfidenceError['errorType']
): number => {
  switch (errorType) {
    case 'confidence_calculation':
      return 3;
    case 'flagging_failed':
      return 5;
    case 'confidence_routing':
      return 2;
    case 'threshold_evaluation':
      return 3;
    case 'metadata_update':
      return 3;
    default:
      return 3;
  }
};

export const isErrorRecoverable = (error: ConfidenceError): boolean => {
  if (!error.recoverable) return false;
  if (error.retryCount >= error.maxRetries) return false;
  if (error.severity === 'critical') return false;
  return true;
};

export const getErrorDisplayInfo = (
  error: ConfidenceError
): {
  title: string;
  message: string;
  icon: string;
  color: string;
  actionRequired: boolean;
} => {
  const baseInfo = {
    confidence_calculation: {
      title: 'Confidence Calculation Failed',
      icon: 'AlertTriangle',
      color: 'orange',
    },
    confidence_routing: {
      title: 'Routing Error',
      icon: 'Route',
      color: 'red',
    },
    flagging_failed: {
      title: 'Flagging Failed',
      icon: 'Flag',
      color: 'yellow',
    },
    threshold_evaluation: {
      title: 'Threshold Evaluation Error',
      icon: 'Target',
      color: 'orange',
    },
    metadata_update: {
      title: 'Metadata Update Failed',
      icon: 'Database',
      color: 'blue',
    },
  };

  const info = baseInfo[error.errorType];
  const actionRequired =
    error.recovery?.actionRequired || error.severity === 'critical';

  return {
    title: info.title,
    message: error.errorMessage,
    icon: info.icon,
    color: error.severity === 'critical' ? 'red' : info.color,
    actionRequired,
  };
};

// Enhanced confidence tracking metadata interfaces (Task #10)
export interface ConfidenceMetadata {
  originalConfidence: number;
  adjustedConfidence: number;
  confidenceLevel: ConfidenceLevel;
  detailedLevel: 'auto_process' | 'high' | 'medium' | 'low' | 'critical';
  confidenceHistory: ConfidenceHistoryEntry[];
  lastEvaluationTimestamp: string;
  evaluationCount: number;
}

export interface ConfidenceHistoryEntry {
  timestamp: string;
  confidence: number;
  level: ConfidenceLevel;
  trigger: 'initial' | 'reprocessing' | 'manual_adjustment' | 'business_rule_penalty';
  adjustmentReason?: string;
  adjustmentAmount?: number;
}

export interface ProcessingMetadata {
  processingAttempts: number;
  lastProcessingAttempt?: string;
  processingErrors: ProcessingError[];
  businessRuleViolations: BusinessRuleViolation[];
  dataQualityScore?: number;
  documentComplexity?: number;
  extractionMethod?: 'OCR' | 'LLM' | 'HYBRID' | 'MANUAL';
  processingDuration?: number; // milliseconds
}

export interface ProcessingError {
  timestamp: string;
  errorType:
    | 'extraction'
    | 'validation'
    | 'submission'
    | 'system'
    | 'confidence'
    | 'routing'
    | 'flagging';
  errorMessage: string;
  errorCode?: string;
  recoverable: boolean;
  retryCount: number;
  maxRetries?: number;
  nextRetryAt?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  context?: Record<string, any>;
}

// Task #16: Confidence-specific error handling interfaces
export interface ConfidenceError {
  id: string;
  timestamp: string;
  errorType:
    | 'confidence_calculation'
    | 'confidence_routing'
    | 'flagging_failed'
    | 'threshold_evaluation'
    | 'metadata_update';
  errorMessage: string;
  errorCode: string;
  caseNumber?: string;
  originalConfidence?: number;
  targetConfidence?: number;
  severity: 'warning' | 'error' | 'critical';
  recoverable: boolean;
  retryCount: number;
  maxRetries: number;
  retryStrategy: RetryStrategy;
  context: {
    operation: string;
    parameters?: Record<string, any>;
    stackTrace?: string;
    userAction?: string;
  };
  recovery?: ErrorRecoveryStrategy;
}

export interface RetryStrategy {
  type: 'immediate' | 'exponential_backoff' | 'fixed_interval' | 'manual_only';
  baseDelay: number; // milliseconds
  maxDelay: number; // milliseconds
  multiplier: number; // for exponential backoff
  jitter: boolean; // add randomness to prevent thundering herd
}

export interface ErrorRecoveryStrategy {
  fallbackBehavior:
    | 'use_cached'
    | 'use_default'
    | 'prompt_user'
    | 'escalate'
    | 'hide_case';
  fallbackValue?: any;
  userMessage: string;
  actionRequired: boolean;
  availableActions: ErrorRecoveryAction[];
  escalationRequired: boolean;
  escalationTarget?: 'supervisor' | 'technical_support' | 'manual_processing';
}

export interface ErrorRecoveryAction {
  id: string;
  label: string;
  action: 'retry' | 'use_fallback' | 'manual_override' | 'escalate' | 'dismiss';
  parameters?: Record<string, any>;
  requiresConfirmation: boolean;
  riskLevel: 'low' | 'medium' | 'high';
}

export interface ConfidenceErrorState {
  hasErrors: boolean;
  errors: ConfidenceError[];
  errorsByCase: Record<string, ConfidenceError[]>;
  criticalErrors: ConfidenceError[];
  isRecovering: boolean;
  lastErrorAt?: string;
  errorSummary: {
    totalErrors: number;
    recoverableErrors: number;
    criticalErrors: number;
    casesAffected: number;
  };
}

export interface BusinessRuleViolation {
  rule: string;
  description: string;
  severity: 'warning' | 'error' | 'critical';
  confidencePenalty: number;
  timestamp: string;
  resolved: boolean;
  resolutionNote?: string;
}

export interface CaseRoutingInfo {
  assignedWorkflow:
    | 'AUTO_PROCESS'
    | 'HIGH_CONFIDENCE'
    | 'MEDIUM_CONFIDENCE'
    | 'LOW_CONFIDENCE'
    | 'MANUAL_ESCALATION';
  estimatedProcessingTime: string;
  requiresCSRReview: boolean;
  requiresFlagging: boolean;
  flaggingReason?: string;
  escalationLevel?: 'none' | 'supervisor' | 'manager' | 'manual';
  routingTimestamp: string;
  workflowStatus: 'pending' | 'in_progress' | 'completed' | 'escalated' | 'failed';
}

export interface QualityAssuranceInfo {
  qaCheckpoints: QACheckpointResult[];
  overallQAStatus: 'pending' | 'passed' | 'failed' | 'bypassed';
  qaCompletionTimestamp?: string;
  finalValidationRequired: boolean;
  supervisorApprovalRequired: boolean;
  supervisorApprovalStatus?: 'pending' | 'approved' | 'rejected';
}

export interface QACheckpointResult {
  checkpoint: 'PRE_PROCESSING' | 'POST_EXTRACTION' | 'PRE_SUBMISSION';
  status: 'pending' | 'passed' | 'failed' | 'skipped';
  timestamp: string;
  checks: QACheckResult[];
  overallScore: number;
}

export interface QACheckResult {
  checkName: string;
  passed: boolean;
  score: number;
  message?: string;
  details?: any;
}

// Enhanced confidence evaluation using new architecture
export const getDetailedConfidenceAssessment = (
  confidence: number
): {
  level: ConfidenceLevel;
  detailed: 'auto_process' | 'high' | 'medium' | 'low' | 'critical';
  isVisible: boolean;
  requiresFlag: boolean;
  recommendedWorkflow: string;
} => {
  const isVisible = isValidForProcessing(confidence);
  let detailed: 'auto_process' | 'high' | 'medium' | 'low' | 'critical';
  let requiresFlag = false;
  let recommendedWorkflow: string;

  if (confidence >= 95) {
    detailed = 'auto_process';
    recommendedWorkflow = 'Automated Processing';
  } else if (confidence >= CONFIDENCE_THRESHOLDS.HIGH_CONFIDENCE) {
    detailed = 'high';
    recommendedWorkflow = 'Quick CSR Review';
  } else if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM_CONFIDENCE) {
    detailed = 'medium';
    recommendedWorkflow = 'Standard CSR Review';
  } else if (confidence >= CONFIDENCE_THRESHOLDS.LOW_CONFIDENCE) {
    detailed = 'low';
    recommendedWorkflow = 'Enhanced CSR Review';
  } else {
    detailed = 'critical';
    requiresFlag = true;
    recommendedWorkflow = 'Manual Escalation';
  }

  if (!isVisible) {
    requiresFlag = true;
  }

  return {
    level: getConfidenceLevel(confidence),
    detailed,
    isVisible,
    requiresFlag,
    recommendedWorkflow,
  };
};

// API Response Types for Confidence Data (Task #10)
export interface CaseWithConfidenceResponse {
  case: PayrollCase;
  confidenceAssessment: {
    score: number;
    level: ConfidenceLevel;
    detailedLevel: 'auto_process' | 'high' | 'medium' | 'low' | 'critical';
    routing: CaseRoutingInfo;
    isVisible: boolean;
    requiresFlag: boolean;
  };
  processingStatus: {
    stage: 'intake' | 'processing' | 'review' | 'submission' | 'complete' | 'escalated';
    progress: number; // 0-100
    estimatedCompletion?: string;
    blockers: string[];
  };
}

export interface ConfidenceAnalyticsResponse {
  summary: {
    totalCases: number;
    averageConfidence: number;
    flaggingRate: number;
    processingEfficiency: number;
  };
  distribution: {
    byLevel: Record<ConfidenceLevel, number>;
    byWorkflow: Record<string, number>;
    byTimeframe: Array<{
      period: string;
      count: number;
      averageConfidence: number;
    }>;
  };
  trends: {
    confidenceTrend: Array<{
      date: string;
      averageConfidence: number;
      caseCount: number;
    }>;
    flaggingTrend: Array<{
      date: string;
      flaggedCount: number;
      totalCount: number;
      flaggingRate: number;
    }>;
  };
  recommendations: string[];
}

export interface CaseBatchEvaluationResponse {
  batchId: string;
  timestamp: string;
  totalCases: number;
  evaluatedCases: Array<{
    caseNumber: string;
    confidence: number;
    level: ConfidenceLevel;
    workflow: string;
    flagged: boolean;
    estimatedTime: string;
  }>;
  summary: {
    autoProcessCases: number;
    csrReviewCases: number;
    flaggedCases: number;
    totalEstimatedTime: string;
  };
}

export interface ConfidenceHistoryResponse {
  caseNumber: string;
  history: ConfidenceHistoryEntry[];
  currentConfidence: number;
  confidenceTrend: 'improving' | 'declining' | 'stable';
  significantEvents: Array<{
    timestamp: string;
    event: string;
    impact: 'positive' | 'negative' | 'neutral';
    description: string;
  }>;
}

export interface QualityAssuranceReportResponse {
  caseNumber: string;
  qaStatus: QualityAssuranceInfo;
  checkpointDetails: Array<{
    checkpoint: string;
    results: QACheckResult[];
    passed: boolean;
    score: number;
    recommendations: string[];
  }>;
  overallAssessment: {
    readyForSubmission: boolean;
    riskLevel: 'low' | 'medium' | 'high';
    requiredActions: string[];
  };
}

// Core API Payroll Reports (Task: Get Payroll Reports Endpoint)
export interface PayrollReport {
  reportType: 'Summary' | 'Register' | 'Exceptions';
  mimeType: string;
  fileName: string;
  data: string; // Base64-encoded PDF data
  sizeBytes: number;
}

export interface PayGroupReports {
  payGroupId: number;
  payGroupName: string;
  employeeCount: number;
  summary: PayrollReport;
  register: PayrollReport;
  exceptions: PayrollReport;
}

export interface PayrollReportsResponse {
  payGroups: PayGroupReports[];
}

// Webhook payload types for confidence updates
export interface ConfidenceUpdateWebhookPayload {
  caseNumber: string;
  newConfidence: number;
  previousConfidence: number;
  trigger: 'reprocessing' | 'manual_adjustment' | 'business_rule_change';
  timestamp: string;
  metadata: {
    adjustmentReason?: string;
    adjustedBy?: string;
    automaticAdjustment: boolean;
    businessRuleViolations?: BusinessRuleViolation[];
  };
}

export interface ProcessingStatusWebhookPayload {
  caseNumber: string;
  status: 'started' | 'in_progress' | 'completed' | 'failed' | 'escalated';
  stage: 'intake' | 'extraction' | 'validation' | 'submission' | 'complete';
  progress: number;
  timestamp: string;
  metadata: {
    processingDuration?: number;
    errors?: ProcessingError[];
    qualityChecks?: QACheckpointResult[];
    routingInfo?: CaseRoutingInfo;
  };
}
